import type { ImgHTMLAttributes } from 'react'

/**
 * Default srcset widths covering common display sizes.
 * Includes: mobile (480), tablet (720), article desktop (854),
 * larger displays (1080, 1440), and 2x retina (1920).
 */
const DEFAULT_SRCSET_WIDTHS = [480, 720, 854, 1080, 1440, 1920]

/**
 * Default sizes for article context with container queries:
 * - Container >= 1024px: 854px (120% width cinematic effect)
 * - Container 768px-1024px: 720px
 * - Below: full viewport width
 */
const DEFAULT_SIZES =
  '(min-width: 1280px) 854px, (min-width: 1024px) 720px, 100vw'

interface ImageProps
  extends Omit<
    ImgHTMLAttributes<HTMLImageElement>,
    'src' | 'srcSet' | 'sizes'
  > {
  /**
   * Image source path relative to the static folder.
   * Example: "images/hero.png" will resolve to "/static/images/hero.png"
   * Example: "/images/hero.png" will also work (leading slash is optional)
   */
  src: string
  /**
   * Image width - used as fallback and for aspect ratio hints.
   */
  width?: number
  /**
   * Image height - used for aspect ratio hints.
   */
  height?: number
  /**
   * Image quality (1-100, default: 75)
   */
  quality?: number
  /**
   * Whether to use Vercel image optimization (default: true in production)
   */
  optimize?: boolean
  /**
   * Array of widths to generate for srcset, or false to disable.
   * Default: [480, 720, 854, 1080, 1440, 1920]
   *
   * @example
   * // Custom widths for a smaller image
   * <Image src="icon.png" srcSet={[200, 400, 600]} />
   *
   * @example
   * // Disable srcset
   * <Image src="logo.png" srcSet={false} />
   */
  srcSet?: number[] | false
  /**
   * Custom sizes attribute for responsive images.
   * Default: "(min-width: 1280px) 854px, (min-width: 1024px) 720px, 100vw"
   *
   * This tells the browser which image size to download based on viewport.
   * Tuned for article layout with cinematic 120% width on large screens.
   *
   * @example
   * // Full-width hero image
   * <Image src="hero.jpg" sizes="100vw" />
   *
   * @example
   * // Fixed-width thumbnail
   * <Image src="thumb.jpg" sizes="200px" srcSet={[200, 400]} />
   */
  sizes?: string
}

/**
 * Image component that utilizes Vercel's image optimization pipeline
 * with responsive srcset support.
 *
 * In production on Vercel, images are automatically optimized via the
 * /_vercel/image endpoint with multiple sizes for responsive loading.
 * In development, images are served directly from the static folder.
 *
 * @example
 * // Basic usage (uses defaults optimized for article layout)
 * <Image src="images/hero.jpg" alt="Hero image" />
 *
 * @example
 * // With explicit dimensions for aspect ratio
 * <Image src="images/hero.jpg" width={800} height={450} alt="Hero image" />
 *
 * @example
 * // Full-width hero with custom sizes
 * <Image src="images/hero.jpg" sizes="100vw" alt="Full width hero" />
 *
 * @example
 * // Small fixed-width image
 * <Image src="images/icon.png" srcSet={[100, 200]} sizes="100px" alt="Icon" />
 */
export function Image({
  src,
  width,
  height,
  quality = 75,
  optimize = process.env.NODE_ENV === 'production',
  srcSet = DEFAULT_SRCSET_WIDTHS,
  sizes = DEFAULT_SIZES,
  style,
  ...props
}: ImageProps) {
  // Normalize src: remove leading slash if present, then add /static/ prefix
  const normalizedSrc = src.startsWith('/') ? src.slice(1) : src
  const staticSrc = normalizedSrc.startsWith('static/')
    ? `/${normalizedSrc}`
    : `/static/${normalizedSrc}`

  /**
   * Construct optimized image URL for Vercel.
   * Uses relative URL which Vercel resolves on the server.
   */
  const getOptimizedUrl = (w?: number) => {
    const params = new URLSearchParams({
      url: staticSrc,
      q: quality.toString(),
    })

    if (w) params.set('w', w.toString())

    return `/_vercel/image?${params.toString()}`
  }

  // Generate srcset string
  const srcSetValue =
    optimize && srcSet && srcSet.length > 0
      ? srcSet.map((w) => `${getOptimizedUrl(w)} ${w}w`).join(', ')
      : undefined

  // Main src: use width if provided, otherwise use largest srcset width
  // Fall back to staticSrc if no width is available (Vercel requires `w` param)
  const resolvedWidth =
    width || (srcSet && srcSet.length > 0 ? Math.max(...srcSet) : undefined)
  const mainSrc =
    optimize && resolvedWidth ? getOptimizedUrl(resolvedWidth) : staticSrc

  // Calculate aspect ratio style to prevent layout shift
  const aspectRatioStyle =
    width && height ? { aspectRatio: `${width} / ${height}` } : undefined

  return (
    <img
      src={mainSrc}
      srcSet={srcSetValue}
      sizes={srcSetValue ? sizes : undefined}
      width={width}
      height={height}
      style={{ ...aspectRatioStyle, ...style }}
      {...props}
    />
  )
}
